import { chipBaseClasses } from '../shared/badges/badgeBase';

/** What a provider card shows: its connection read, or why it cannot be read or made. */
export type IntegrationStatus = 'connected' | 'reconnect' | 'disconnected' | 'unknown' | 'unavailable';

export interface IntegrationConnection {
  /** null = the status read failed (INT-03): the card offers Retry, never Connect. */
  connected: boolean | null;
  connecting: boolean;
  disconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onRetry?: () => void;
  /** Set on a connection authorized before two-way sync existed: the sentence says why, Reconnect re-runs Connect. */
  reconnectMessage?: string;
  available?: boolean;
  unavailableReason?: string | null;
  /** When the connection was made (Integration.createdAt), for "since" on the Status row. */
  connectedAt?: string | null;
  /** The connection's own rows (Site, Workspace, Account); a row without a value is left out. */
  rows?: ReadonlyArray<readonly [label: string, value: string | null | undefined]>;
  /** Trackers whose two-way sync can need a reconnect (Jira, Linear) show a Two-way sync row. */
  twoWaySync?: boolean;
}

export function integrationStatus(connection: IntegrationConnection): IntegrationStatus {
  if (connection.connected === null) return 'unknown';
  if (connection.connected) return connection.reconnectMessage ? 'reconnect' : 'connected';
  return connection.available === false ? 'unavailable' : 'disconnected';
}

// The chip base classes with the audited status pairs (StatusBadge.tsx), its 5 x 10 dot.
const CHIP: Record<IntegrationStatus, { label: string; tone: string }> = {
  connected: { label: 'Connected', tone: 'bg-[var(--color-status-resolved-bg)] text-[var(--color-status-resolved-text)]' },
  reconnect: { label: 'Reconnect needed', tone: 'bg-[var(--color-status-in-progress-bg)] text-[var(--color-status-in-progress-text)]' },
  disconnected: { label: 'Not connected', tone: 'bg-[var(--color-status-closed-bg)] text-[var(--color-status-closed-text)]' },
  unknown: { label: "Can't check", tone: 'bg-[var(--color-status-closed-bg)] text-[var(--color-status-closed-text)]' },
  unavailable: { label: 'Not available yet', tone: 'bg-[var(--color-status-closed-bg)] text-[var(--color-status-closed-text)]' },
};

export function IntegrationStatusChip({ status }: { status: IntegrationStatus }) {
  return (
    <span className={`${chipBaseClasses} gap-1 ${CHIP[status].tone}`} data-status={status}>
      <svg viewBox="0 0 5 10" className="w-[5px] h-[10px] flex-shrink-0" aria-hidden="true"><circle cx="2.5" cy="5" r="2.5" fill="currentColor" /></svg>
      {CHIP[status].label}
    </span>
  );
}
